/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// Maintenance — v77.99.32
//
// Top-level page for the Maintenance Payments feature. Three tabs:
//   * Overview     — dashboard summary cards + recent activity
//   * Schedule     — upcoming + past occurrences with one-tap record
//   * Arrangements — manage active arrangements (setup / edit / end)
//
// Privacy-first throughout: arrangements default `is_shared:false`,
// individual payment status updates also default private, and there's
// an explicit "Share this update with co-parent" toggle on every
// record action.
//

/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: standard "fetch / sync on dependency change" useEffects
 * with setState in async IIFEs. eslint-plugin-react-hooks v5's
 * set-state-in-effect rule is over-eager for these patterns. Other
 * React-19 lints (rules-of-hooks, exhaustive-deps) remain enabled.
 */
// All numbers shown to a restricted co-parent are scoped server-side
// via `_visible_arrangement` so per-child amounts for unauthorised
// children never appear on this page even when the underlying
// arrangement covers them.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { currencySymbol } from "@/lib/format";
import { ChildScopeBadge } from "@/components/ChildScopeBadge";
import { MediatorPdfDownloadButton } from "@/components/mediator/MediatorPdfDownloadButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HandCoins, Plus, AlertTriangle, CheckCircle2, Clock, EyeOff, Eye, Share2, FileDown, FileText, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/downloadFile";
import PaymentHistorySheet from "@/components/maintenance/PaymentHistorySheet";
import { ParentName } from "@/components/ParentName";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v77.99.34 — Date-range presets for the Schedule view + exports.
// Each preset returns { from, to } in YYYY-MM-DD. "All time" maps
// to a wide-open window the backend will clamp to its 5-year safety
// cap. "Custom" is handled inline (date inputs).
function ymd(d) { return d.toISOString().slice(0, 10); }
const RANGE_PRESETS = [
  { id: "this_month", label: "This month", compute: () => {
    const now = new Date();
    return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
             to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }},
  { id: "last_3m", label: "Last 3 months", compute: () => {
    const now = new Date();
    return { from: ymd(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
             to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }},
  { id: "last_12m", label: "Last 12 months", compute: () => {
    const now = new Date();
    return { from: ymd(new Date(now.getFullYear() - 1, now.getMonth(), 1)),
             to: ymd(now) };
  }},
  { id: "ytd", label: "Year to date", compute: () => {
    const now = new Date();
    return { from: `${now.getFullYear()}-01-01`, to: ymd(now) };
  }},
  { id: "all", label: "All time", compute: () => ({ from: "2020-01-01", to: "2099-12-31" }) },
];
// v77.99.45 — Overview tab has its own, smaller preset list. The Schedule
// tab needs "Last 3 months" + "All time" because it's an audit surface,
// whereas Overview is a quick snapshot ("This month" by default).
const OVERVIEW_RANGE_PRESETS = [
  { id: "this_month", label: "This month", compute: () => {
    const now = new Date();
    return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
             to: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  }},
  { id: "ytd", label: "Year to date", compute: () => {
    const now = new Date();
    return { from: `${now.getFullYear()}-01-01`, to: ymd(now) };
  }},
  { id: "this_year", label: "This year", compute: () => {
    const now = new Date();
    return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` };
  }},
  { id: "last_12m", label: "Last 12 months", compute: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return { from: ymd(start), to: ymd(now) };
  }},
];
const STATUS_OPTIONS = [
  { id: "due", label: "Due" },
  { id: "late", label: "Overdue" },
  { id: "missed", label: "Missed" },
  { id: "paid", label: "Paid on time" },
  { id: "paid_late", label: "Paid late" },
  { id: "part_paid", label: "Part-paid" },
  { id: "overpaid", label: "Overpaid" },
  { id: "waived", label: "Waived" },
  { id: "disputed", label: "Disputed" },
];

const FREQ_LABEL = { weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly" };
const STATUS_LABEL = {
  due: "Due", paid: "Paid on time", paid_late: "Paid late", part_paid: "Part-paid", late: "Overdue",
  missed: "Missed", waived: "Waived", disputed: "Disputed", overpaid: "Overpaid",
};
const STATUS_TONE = {
  due:       "bg-[#EEF3F4] text-[#2E3A3F] border-[#D9E1E3]",
  paid:      "bg-[#E0F2F2] text-[#0F5658] border-[#A6D7D9]",
  // v77.99.37 — paid_late uses a distinct amber palette so a paid-but-late
  // payment is never visually conflated with on-time success.
  paid_late: "bg-[#FFEDBF] text-[#6F4E1E] border-[#C99A2A]",
  overpaid:  "bg-[#E0F2F2] text-[#0F5658] border-[#A6D7D9]",
  part_paid: "bg-[#FFF2D9] text-[#7A5A18] border-[#E6C988]",
  late:      "bg-[#FFE5D9] text-[#883417] border-[#E6A481]",
  missed:    "bg-[#FBEFEE] text-[#7A2418] border-[#D9837A]",
  waived:    "bg-[#EEF3F4] text-[#6F828A] border-[#D9E1E3]",
  disputed:  "bg-[#F0E8F5] text-[#503A6E] border-[#C3A8E0]",
};

function fmtMoney(n, currency = "EUR") {
  if (n == null) return "—";
  const sym = currencySymbol(currency);
  // Suffix-style codes (kr, CHF, CA$, A$, NZ$) read better with a
  // trailing space; symbol-style codes (€, $, £, ¥) sit flush.
  const needsSpace = sym.length > 1 && !["CA$", "A$", "NZ$"].includes(sym);
  return needsSpace ? `${sym} ${Number(n).toFixed(2)}` : `${sym}${Number(n).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function Maintenance() {
  const { user, isMediator } = useAuth();
  const [tab, setTab] = useState("overview");
  const [arrangements, setArrangements] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [editingArr, setEditingArr] = useState(null);
  const [recordingFor, setRecordingFor] = useState(null);
  // v77.99.41 — Payment history sheet (per-occurrence audit trail).
  const [historyFor, setHistoryFor] = useState(null);
  // v77.99.33 — Bulk action state.
  //   * `selected`: Set of payment ids currently checked. Drives both
  //     the action bar's "X selected" copy and the dialog payload.
  //   * `bulkAction`: the action chosen from the bar; when non-null
  //     the BulkConfirmDialog renders.
  //   * `settleHistoric`: when non-null, the historic-settle flow is
  //     active; holds cutoff_date + the server preview.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [settleHistoric, setSettleHistoric] = useState(null);
  // v77.99.34 — Schedule tab filters. `rangeId` drives the preset
  // pill row; `custom` is only consulted when rangeId === "custom".
  // `statuses` is a Set of status ids to keep; empty = show all.
  const [rangeId, setRangeId] = useState("last_3m");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [statuses, setStatuses] = useState(() => new Set());
  const [scheduleLoading, setScheduleLoading] = useState(false);
  // v77.99.45 — Overview tab now has its own date-range picker so the
  // user can re-scope Due / Paid / Outstanding / Late / Missed across
  // longer windows (year-to-date, this year, last 12m, custom). Lives
  // separately from the Schedule filter so switching tabs doesn't
  // collapse one surface's context into the other.
  const [overviewRangeId, setOverviewRangeId] = useState("this_month");
  const [overviewCustom, setOverviewCustom] = useState({ from: "", to: "" });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const activeRange = useMemo(() => {
    if (rangeId === "custom") return { from: custom.from || "", to: custom.to || "" };
    const p = RANGE_PRESETS.find((r) => r.id === rangeId) || RANGE_PRESETS[1];
    return p.compute();
  }, [rangeId, custom]);
  // v77.99.45 — Resolved overview range used to label cards and drive
  // the summary refetch. Custom requires both ends; otherwise we fall
  // back to "This month".
  const overviewActiveRange = useMemo(() => {
    if (overviewRangeId === "custom") return { from: overviewCustom.from || "", to: overviewCustom.to || "" };
    const p = OVERVIEW_RANGE_PRESETS.find((r) => r.id === overviewRangeId) || OVERVIEW_RANGE_PRESETS[0];
    return p.compute();
  }, [overviewRangeId, overviewCustom]);
  // Human-friendly suffix appended to "Due"/"Paid" card labels. Empty
  // string for "This month" so the legacy label "Due this month" is
  // preserved verbatim.
  const overviewRangeLabel = useMemo(() => {
    if (overviewRangeId === "this_month") return "this month";
    const preset = OVERVIEW_RANGE_PRESETS.find((r) => r.id === overviewRangeId);
    if (preset) return preset.label.toLowerCase();
    return "selected range";
  }, [overviewRangeId]);
  const toggleStatus = (id) => {
    setStatuses((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelected = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const memberName = (id) => members.find((m) => m.id === id)?.name || "—";
  const childName = (id) => children.find((c) => c.id === id)?.name || "—";

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeRange.from) params.set("from", activeRange.from);
      if (activeRange.to) params.set("to", activeRange.to);
      if (statuses.size > 0) params.set("status", Array.from(statuses).join(","));
      const qs = params.toString();
      // v77.99.45 — Summary now respects the Overview range.
      const sumParams = new URLSearchParams();
      if (overviewActiveRange.from) sumParams.set("from", overviewActiveRange.from);
      if (overviewActiveRange.to) sumParams.set("to", overviewActiveRange.to);
      const sumQs = sumParams.toString();
      const [a, p, s, m, c] = await Promise.all([
        api.get("/maintenance/arrangements"),
        api.get(`/maintenance/schedule${qs ? `?${qs}` : ""}`),
        api.get(`/maintenance/summary${sumQs ? `?${sumQs}` : ""}`),
        api.get("/household").catch(() => ({ data: { members: [] } })),
        api.get("/children").catch(() => ({ data: [] })),
      ]);
      setArrangements(a.data.arrangements || []);
      setPayments(p.data.payments || []);
      setSummary(s.data || null);
      setMembers(m.data?.members || m.data || []);
      setChildren(Array.isArray(c.data) ? c.data : (c.data?.children || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load maintenance data.");
    } finally {
      setLoading(false);
    }
  };

  // v77.99.34 — When the filter changes on the Schedule tab, fetch
  // only the payments (arrangements/summary/people are static for
  // this view), with a small "refreshing" spinner state distinct
  // from the top-level loading state used on first paint.
  const reloadSchedule = async () => {
    setScheduleLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeRange.from) params.set("from", activeRange.from);
      if (activeRange.to) params.set("to", activeRange.to);
      if (statuses.size > 0) params.set("status", Array.from(statuses).join(","));
      const qs = params.toString();
      const r = await api.get(`/maintenance/schedule${qs ? `?${qs}` : ""}`);
      setPayments(r.data.payments || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load schedule.");
    } finally {
      setScheduleLoading(false);
    }
  };

  // v77.99.45 — Range-aware summary refetch. Triggered when the
  // Overview date range changes (preset pill or Custom inputs).
  const reloadSummary = async () => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (overviewActiveRange.from) params.set("from", overviewActiveRange.from);
      if (overviewActiveRange.to) params.set("to", overviewActiveRange.to);
      const qs = params.toString();
      const r = await api.get(`/maintenance/summary${qs ? `?${qs}` : ""}`);
      setSummary(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to refresh summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    // Avoid double-fetching on first mount (loadAll already fetched).
    if (loading) return;
    reloadSchedule();
  }, [rangeId, custom.from, custom.to, Array.from(statuses).sort().join("|")]);
  useEffect(() => {
    // Avoid double-fetching on first mount (loadAll already fetched).
    if (loading) return;
    reloadSummary();
  }, [overviewRangeId, overviewCustom.from, overviewCustom.to]);

  const upcoming = useMemo(
    () => payments.filter((p) => ["due", "late"].includes(p.status)).slice(0, 5),
    [payments],
  );
  const recent = useMemo(
    () => payments.filter((p) => ["paid", "part_paid", "missed", "waived", "disputed", "overpaid"].includes(p.status)).slice(-5).reverse(),
    [payments],
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-6xl mx-auto min-w-0 w-full overflow-x-hidden" data-testid="maintenance-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B2E32] flex items-center gap-2 flex-wrap">
            <HandCoins className="w-7 h-7 text-[#116B6E] flex-shrink-0" strokeWidth={1.6} />
            Maintenance
            <ChildScopeBadge testid="maintenance-child-scope-badge" />
          </h1>
          <p className="text-sm text-[#6F828A] mt-1">
            Record and track maintenance payments. Private by default — you decide what to share.
          </p>
        </div>
        {!isMediator && (
          <Button
            onClick={() => { setEditingArr(null); setShowSetup(true); }}
            className="bg-[#116B6E] hover:bg-[#0F5658] text-white whitespace-nowrap flex-shrink-0"
            data-testid="maintenance-add-arrangement-button"
          >
            <Plus className="w-4 h-4 mr-1" strokeWidth={1.8} />
            Add arrangement
          </Button>
        )}
        {/* v77.99.70 — Mediator-only header CTA. Existing per-section
            Export PDF and Late-payment Evidence PDF buttons stay
            visible to them inside the page; this header button gives
            a one-click "official PDF" affordance for the full record. */}
        <MediatorPdfDownloadButton
          to="/app/court-bundle"
          label="Download read-only PDF"
          testid="maintenance-mediator-pdf"
          className="flex-shrink-0"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#E8F0F1] rounded-sm">
          <TabsTrigger value="overview" data-testid="tab-maintenance-overview">Overview</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-maintenance-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="arrangements" data-testid="tab-maintenance-arrangements">Arrangements</TabsTrigger>
        </TabsList>

        {/* ---- OVERVIEW ---- */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* v77.99.45 — Overview date range picker. Sits above the
              summary cards so the whole snapshot retunes to whatever
              window the user picks. "This month" by default keeps the
              quick-glance behaviour intact. */}
          <Card data-testid="maintenance-overview-range">
            <CardContent className="p-3 space-y-2">
              <div className="flex flex-wrap gap-2 items-center">
                <Filter className="w-3.5 h-3.5 text-[#6F828A] flex-shrink-0" strokeWidth={1.8} />
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Period</span>
                {OVERVIEW_RANGE_PRESETS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setOverviewRangeId(r.id)}
                    className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${overviewRangeId === r.id ? "bg-[#116B6E] text-white border-[#116B6E]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
                    data-testid={`maintenance-overview-range-${r.id}`}
                  >
                    {r.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setOverviewRangeId("custom")}
                  className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${overviewRangeId === "custom" ? "bg-[#116B6E] text-white border-[#116B6E]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
                  data-testid="maintenance-overview-range-custom"
                >
                  Custom
                </button>
                {summaryLoading && (
                  <span className="text-[11px] text-[#6F828A] ml-1" data-testid="maintenance-overview-summary-loading">Refreshing…</span>
                )}
              </div>
              {overviewRangeId === "custom" && (
                <div className="grid grid-cols-2 gap-2 items-end pt-1" data-testid="maintenance-overview-custom-inputs">
                  <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">From</Label>
                    <Input type="date" value={overviewCustom.from} onChange={(e) => setOverviewCustom({ ...overviewCustom, from: e.target.value })} className="w-full" data-testid="maintenance-overview-custom-from" />
                  </div>
                  <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">To</Label>
                    <Input type="date" value={overviewCustom.to} onChange={(e) => setOverviewCustom({ ...overviewCustom, to: e.target.value })} className="w-full" data-testid="maintenance-overview-custom-to" />
                  </div>
                </div>
              )}
              {(summary?.range_from || summary?.range_to) && (
                <div className="text-[11px] text-[#6F828A]" data-testid="maintenance-overview-active-range">
                  Showing <span className="font-medium text-[#2E3A3F]">{overviewRangeLabel}</span>
                  {summary?.range_from && summary?.range_to && (
                    <> · {fmtDate(summary.range_from)} → {fmtDate(summary.range_to)}</>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {summary && (
            <>
              {/* v77.99.158 (iter 136.ml-arrears) — Four canonical
                  Overview cards. The ARREARS card is visually
                  emphasised when > 0. The old "Outstanding" +
                  "Late/Missed" + separate "Upcoming & outstanding"
                  cards are replaced by a single authoritative
                  reading from the server-side ledger. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="maintenance-overview-cards">
                <SummaryCard
                  label="Due, year to date"
                  value={fmtMoney(summary.due_ytd ?? summary.due_this_month, summary.currency)}
                  tone="neutral"
                  testid="maintenance-summary-due-ytd"
                />
                <SummaryCard
                  label="Paid, year to date"
                  value={fmtMoney(summary.paid_ytd ?? summary.paid_this_month, summary.currency)}
                  tone="paid"
                  testid="maintenance-summary-paid-ytd"
                />
                <div
                  className={`rounded-md border p-3 flex flex-col justify-between min-h-[94px] ${
                    (summary.arrears_total ?? summary.outstanding_total ?? 0) > 0
                      ? "bg-[#FBEFEE] border-[#E6BAB6] shadow-sm"
                      : "bg-white border-[#D9E1E3]"
                  }`}
                  data-testid="maintenance-summary-arrears"
                >
                  <div className={`text-[10px] uppercase tracking-[0.1em] font-semibold ${
                    (summary.arrears_total ?? 0) > 0 ? "text-[#A93A2F]" : "text-[#6F828A]"
                  }`}>
                    Arrears
                  </div>
                  <div className={`font-heading text-2xl font-bold ${
                    (summary.arrears_total ?? 0) > 0 ? "text-[#5C1F19]" : "text-[#0B2E32]"
                  }`}>
                    {fmtMoney(summary.arrears_total ?? summary.outstanding_total ?? 0, summary.currency)}
                  </div>
                  {summary.arrears_context_text && (summary.arrears_total ?? 0) > 0 && (
                    <div className="text-[11px] leading-snug text-[#7A4B45] mt-1" data-testid="maintenance-summary-arrears-context">
                      {summary.arrears_context_text}
                    </div>
                  )}
                </div>
                <SummaryCard
                  label={summary.current_due_date ? "Current amount due" : "Next due"}
                  value={
                    summary.current_due_amount != null
                      ? fmtMoney(summary.current_due_amount, summary.currency)
                      : summary.next_due_amount != null
                        ? fmtMoney(summary.next_due_amount, summary.currency)
                        : "—"
                  }
                  tone={summary.current_due_date ? "warn" : "neutral"}
                  testid="maintenance-summary-current-next"
                />
              </div>
            </>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <Card data-testid="maintenance-overview-next">
              <CardHeader><CardTitle className="text-base">Next due</CardTitle></CardHeader>
              <CardContent>
                {summary?.next_due_date ? (
                  <div>
                    <div className="font-heading text-2xl font-bold text-[#0B2E32]">
                      {fmtMoney(summary.next_due_amount, summary.currency)}
                    </div>
                    <div className="text-sm text-[#6F828A]">on {fmtDate(summary.next_due_date)}</div>
                  </div>
                ) : (
                  <div className="text-sm text-[#6F828A]">No upcoming payments.</div>
                )}
              </CardContent>
            </Card>
            <Card data-testid="maintenance-overview-last">
              <CardHeader><CardTitle className="text-base">Last paid</CardTitle></CardHeader>
              <CardContent>
                {summary?.last_paid_date ? (
                  <div>
                    <div className="font-heading text-2xl font-bold text-[#0B2E32]">
                      {fmtMoney(summary.last_paid_amount, summary.currency)}
                    </div>
                    <div className="text-sm text-[#6F828A]">on {fmtDate(summary.last_paid_date)}</div>
                  </div>
                ) : (
                  <div className="text-sm text-[#6F828A]">No payments recorded yet.</div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Overdue maintenance</CardTitle>
            </CardHeader>
            <CardContent>
              {/* v77.99.158 (iter 136.ml-arrears) — Shows only past-due
                  rows with a remaining shortfall. "All caught up"
                  wording removed when there's genuinely nothing
                  overdue (previously contradicted the Arrears card). */}
              {upcoming.filter((u) => (u.status === "late" || u.status === "missed" || u.status === "part_paid")).length === 0 ? (
                <div className="text-sm text-[#6F828A]" data-testid="maintenance-overview-all-caught-up">
                  No overdue maintenance.
                </div>
              ) : (
                <PaymentList
                  rows={upcoming.filter((u) => (u.status === "late" || u.status === "missed" || u.status === "part_paid"))}
                  arrangements={arrangements}
                  childName={childName}
                  onRecord={setRecordingFor}
                  onShowHistory={setHistoryFor}
                />
              )}
            </CardContent>
          </Card>
          {recent.length > 0 && (
            <RecentActivityCard
              recent={recent}
              arrangements={arrangements}
              childName={childName}
              onRecord={setRecordingFor}
              onShowHistory={setHistoryFor}
            />
          )}
        </TabsContent>

        {/* ---- SCHEDULE ---- */}
        <TabsContent value="schedule" className="mt-4 space-y-3">
          {/* v77.99.34 — Filter & export bar. Date-range preset pills
              + status chips + CSV/PDF export buttons. The filters
              re-fetch the schedule via the backend so historic
              months that previously couldn't be reached (the default
              window only looked back 60d) are now accessible. */}
          <Card data-testid="maintenance-schedule-filters" className="overflow-hidden">
            <CardContent className="p-3 space-y-3 max-w-full">
              <div className="flex flex-wrap gap-2 items-center">
                <Filter className="w-3.5 h-3.5 text-[#6F828A] flex-shrink-0" strokeWidth={1.8} />
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Period</span>
                {RANGE_PRESETS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRangeId(r.id)}
                    className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${rangeId === r.id ? "bg-[#116B6E] text-white border-[#116B6E]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
                    data-testid={`maintenance-range-${r.id}`}
                  >
                    {r.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setRangeId("custom")}
                  className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${rangeId === "custom" ? "bg-[#116B6E] text-white border-[#116B6E]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
                  data-testid="maintenance-range-custom"
                >
                  Custom
                </button>
              </div>
              {rangeId === "custom" && (
                <div className="grid grid-cols-2 gap-2 items-end" data-testid="maintenance-range-custom-inputs">
                  <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">From</Label>
                    <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="w-full" data-testid="maintenance-range-custom-from" />
                  </div>
                  <div className="min-w-0">
                    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">To</Label>
                    <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="w-full" data-testid="maintenance-range-custom-to" />
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Status</span>
                {STATUS_OPTIONS.map((s) => {
                  const active = statuses.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStatus(s.id)}
                      className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${active ? "bg-[#0B2E32] text-white border-[#0B2E32]" : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#F4FAFA]"}`}
                      data-testid={`maintenance-status-${s.id}`}
                    >
                      {s.label}
                    </button>
                  );
                })}
                {statuses.size > 0 && (
                  <button type="button" onClick={() => setStatuses(new Set())} className="text-xs text-[#6F828A] underline" data-testid="maintenance-status-clear">
                    Clear
                  </button>
                )}
              </div>
              {/* v77.99.45 — Export bar restructured for mobile. Count
                  text sits on its own line at narrow widths; export
                  buttons stack into a responsive grid (1 col @ 360px,
                  3 cols @ sm+) so they never overflow the viewport
                  or push horizontal scroll. */}
              <div className="pt-2 border-t border-[#EEF3F4] space-y-2">
                <div className="text-xs text-[#6F828A] min-w-0 break-words" data-testid="maintenance-schedule-count">
                  {scheduleLoading ? "Loading…" : `${payments.length} occurrence${payments.length === 1 ? "" : "s"} · ${activeRange.from || "—"} → ${activeRange.to || "—"}`}
                </div>
                <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto justify-center"
                    onClick={() => {
                      const qs = new URLSearchParams();
                      if (activeRange.from) qs.set("from", activeRange.from);
                      if (activeRange.to) qs.set("to", activeRange.to);
                      if (statuses.size > 0) qs.set("status", Array.from(statuses).join(","));
                      const url = `${API}/maintenance/export.csv${qs.toString() ? `?${qs.toString()}` : ""}`;
                      downloadFile(url, `coparentconsole_maintenance_${activeRange.from || "all"}_${activeRange.to || "all"}.csv`, { label: "Preparing CSV", successMessage: "CSV ready" });
                    }}
                    data-testid="maintenance-export-csv-button"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} /> Export CSV
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto justify-center"
                    onClick={() => {
                      const qs = new URLSearchParams();
                      if (activeRange.from) qs.set("from", activeRange.from);
                      if (activeRange.to) qs.set("to", activeRange.to);
                      if (statuses.size > 0) qs.set("status", Array.from(statuses).join(","));
                      const url = `${API}/maintenance/export.pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
                      // Iter 125.hybrid-exports — hybrid email fallback.
                      // If the sync export hasn't returned in 15s the
                      // toast offers "Email it to me instead"; at 30s
                      // we auto-queue the background job so the user
                      // is never left staring at a spinner.
                      downloadFile(url, `coparentconsole_maintenance_${activeRange.from || "all"}_${activeRange.to || "all"}.pdf`, {
                        label: "Generating PDF",
                        successMessage: "PDF ready",
                        asyncFallback: {
                          endpoint: "/exports/maintenance/pdf/start",
                          payload: {
                            variant: "schedule",
                            from_date: activeRange.from || null,
                            to_date: activeRange.to || null,
                            status: statuses.size > 0 ? Array.from(statuses).join(",") : null,
                          },
                        },
                      });
                    }}
                    data-testid="maintenance-export-pdf-button"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} /> Export PDF
                  </Button>
                  {/* v77.99.38 — Late payment evidence mini-PDF.
                      Pulls only paid_late + overdue + missed rows
                      in the selected window, signed with the same
                      sha256 fingerprint as Court Bundle so a
                      solicitor or family mediator can verify
                      authenticity. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full sm:w-auto justify-center border-[#C99A2A] text-[#6F4E1E] hover:bg-[#FBEFCC]"
                    onClick={() => {
                      const qs = new URLSearchParams();
                      if (activeRange.from) qs.set("from", activeRange.from);
                      if (activeRange.to) qs.set("to", activeRange.to);
                      const url = `${API}/maintenance/late-evidence.pdf${qs.toString() ? `?${qs.toString()}` : ""}`;
                      downloadFile(url, `coparentconsole_maintenance_late_payments_${activeRange.from || "all"}_${activeRange.to || "all"}.pdf`, {
                        label: "Building late-payment record",
                        successMessage: "Late-payment record ready",
                        asyncFallback: {
                          endpoint: "/exports/maintenance/pdf/start",
                          payload: {
                            variant: "late_evidence",
                            from_date: activeRange.from || null,
                            to_date: activeRange.to || null,
                          },
                        },
                      });
                    }}
                    data-testid="maintenance-export-late-evidence-button"
                    title="A single-page A4 PDF of every late, overdue or missed payment in the chosen window. Designed for mediator / solicitor handover."
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} /> Late-payment record
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* v77.99.91 — Quick-pick selection presets. The selection
              step is the slowest part of the bulk flow when settling
              a year of arrears — these one-tap buttons select all the
              currently-visible rows that match a common predicate.
              They respect the active range + status filters so the
              user retains control of the scope. */}
          <div
            className="flex flex-wrap gap-2 items-center"
            data-testid="maintenance-quickpick-bar"
          >
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mr-0.5">
              Quick-pick
            </span>
            {(() => {
              const isUnsettled = (s) => s === "due" || s === "late" || s === "missed" || s === "part_paid";
              const thisYear = new Date().getFullYear().toString();
              const presets = [
                { id: "all-visible", label: `All visible (${payments.length})`,
                  predicate: () => true },
                { id: "overdue", label: "All overdue",
                  predicate: (p) => p.status === "late" || p.status === "missed" },
                { id: "unsettled", label: "All unsettled",
                  predicate: (p) => isUnsettled(p.status) || !p.status },
                { id: "this-year", label: `Due in ${thisYear}`,
                  predicate: (p) => (p.due_date || "").startsWith(thisYear) },
              ];
              return presets.map((preset) => {
                const matchIds = payments
                  .filter(preset.predicate)
                  .map((p) => p.id);
                const count = matchIds.length;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    disabled={count === 0}
                    onClick={() => setSelected(new Set(matchIds))}
                    className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap transition-colors ${
                      count === 0
                        ? "bg-white text-[#B7C3C7] border-[#EBEFF0] cursor-not-allowed"
                        : "bg-white text-[#116B6E] border-[#A6D7D9] hover:bg-[#F0FAFA]"
                    }`}
                    data-testid={`maintenance-quickpick-${preset.id}`}
                  >
                    {preset.label}{count > 0 && preset.id !== "all-visible" ? ` (${count})` : ""}
                  </button>
                );
              });
            })()}
          </div>

          {/* v77.99.33 — Bulk action bar. Renders when 1+ payments are
              selected via the per-row checkboxes. All actions go
              through a confirmation modal before applying. The
              "Settle historic" button is always available regardless
              of selection state — it's a separate cut-off-date flow
              for outstanding payments. */}
          <div className="flex flex-wrap gap-2 items-center" data-testid="maintenance-bulk-bar">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSettleHistoric({ cutoff_date: new Date().toISOString().slice(0, 10), preview: null, loading: false })}
              data-testid="maintenance-settle-historic-button"
            >
              Settle historic payments…
            </Button>
            {selected.size > 0 && (
              <>
                <div className="text-sm text-[#0B2E32] font-medium" data-testid="maintenance-bulk-count">
                  {selected.size} selected
                </div>
                <Button size="sm" className="bg-[#116B6E] hover:bg-[#0F5658] text-white" onClick={() => setBulkAction("paid")} data-testid="maintenance-bulk-paid">
                  Mark as paid
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkAction("waived")} data-testid="maintenance-bulk-waived">
                  Mark as waived
                </Button>
                <Button size="sm" variant="outline" onClick={() => setBulkAction("disputed")} data-testid="maintenance-bulk-disputed">
                  Mark as disputed
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="maintenance-bulk-clear">
                  Clear
                </Button>
              </>
            )}
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Payment schedule</CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={loadAll}
                data-testid="maintenance-refresh-button"
              >
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {loading || scheduleLoading ? (
                <div className="text-sm text-[#6F828A]">Loading…</div>
              ) : payments.length === 0 ? (
                <div className="text-sm text-[#6F828A]" data-testid="maintenance-schedule-empty">
                  No payments in this period. Try a wider date range or different status filter.
                </div>
              ) : (
                <PaymentList
                  rows={payments}
                  arrangements={arrangements}
                  childName={childName}
                  onRecord={setRecordingFor}
                  onShowHistory={setHistoryFor}
                  selected={selected}
                  onToggleSelected={toggleSelected}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- ARRANGEMENTS ---- */}
        <TabsContent value="arrangements" className="mt-4 space-y-3">
          {arrangements.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <HandCoins className="w-12 h-12 mx-auto text-[#A6D7D9] mb-3" strokeWidth={1.4} />
                <div className="font-heading text-lg font-medium text-[#0B2E32]">No arrangements yet</div>
                <div className="text-sm text-[#6F828A] mt-1 mb-4">
                  Start by adding a maintenance arrangement. It stays private until you choose to share it.
                </div>
                {!isMediator && (
                  <Button onClick={() => { setEditingArr(null); setShowSetup(true); }} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="maintenance-empty-add-button">
                    <Plus className="w-4 h-4 mr-1" /> Add arrangement
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            arrangements.map((arr) => (
              <ArrangementCard
                key={arr.id}
                arr={arr}
                members={members}
                memberName={memberName}
                childName={childName}
                onEdit={() => { setEditingArr(arr); setShowSetup(true); }}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {showSetup && (
        <ArrangementSetupDialog
          open={showSetup}
          onClose={() => { setShowSetup(false); setEditingArr(null); }}
          onSaved={() => { setShowSetup(false); setEditingArr(null); loadAll(); }}
          members={members}
          childList={children}
          editing={editingArr}
          currentUserId={user?.id}
        />
      )}
      {recordingFor && (
        <PaymentRecordDialog
          open={!!recordingFor}
          payment={recordingFor}
          arrangement={arrangements.find((a) => a.id === recordingFor.arrangement_id)}
          onClose={() => setRecordingFor(null)}
          onSaved={() => { setRecordingFor(null); loadAll(); }}
        />
      )}
      {bulkAction && (
        <BulkConfirmDialog
          open={!!bulkAction}
          status={bulkAction}
          paymentIds={Array.from(selected)}
          payments={payments}
          onClose={() => setBulkAction(null)}
          onSaved={() => { setBulkAction(null); clearSelection(); loadAll(); }}
        />
      )}
      {settleHistoric && (
        <SettleHistoricDialog
          state={settleHistoric}
          setState={setSettleHistoric}
          onClose={() => setSettleHistoric(null)}
          onSaved={() => { setSettleHistoric(null); clearSelection(); loadAll(); }}
        />
      )}
      {historyFor && (
        <PaymentHistorySheet
          open={!!historyFor}
          paymentId={historyFor.id}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

// v77.99.61 — Calmer Recent activity card. The previous version
// surfaced 5+ MISSED red badges immediately under the headline
// numbers, which was a jarring entry point for separated parents. We
// now lead with a single "Next action" row (the most-recent row that
// still needs attention) and tuck the full history behind a
// collapsed "View full history" disclosure. Mediators and detail
// users can expand; everyday users get a calm summary first.
function RecentActivityCard({ recent, arrangements, childName, onRecord, onShowHistory }) {
  const KEY = "cpc_maintenance_recent_open";
  const [open, setOpenState] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch (e) { console.debug("[maint] localStorage read failed:", e?.message); return false; }
  });
  const setOpen = (v) => {
    setOpenState(v);
    try { localStorage.setItem(KEY, v ? "1" : "0"); } catch (e) { console.debug("[maint] localStorage write failed:", e?.message); }
  };
  // Pick the most-recent "needs attention" row — that's the user's
  // next action. Falls back to the most recent row in the list.
  const needsAttention = recent.find((r) => ["missed", "late", "paid_late"].includes(r.status));
  const featured = needsAttention || recent[0];
  const featuredArr = featured ? arrangements.find((a) => a.id === featured.arrangement_id) : null;

  // Calm rollup line — counts only, no all-caps badges.
  const counts = recent.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const calmBits = [];
  if (counts.paid || counts.overpaid) calmBits.push(`${(counts.paid || 0) + (counts.overpaid || 0)} paid on time`);
  if (counts.paid_late) calmBits.push(`${counts.paid_late} paid late`);
  if (counts.late) calmBits.push(`${counts.late} past due`);
  if (counts.missed) calmBits.push(`${counts.missed} missed`);
  const rollup = calmBits.join(" · ") || "Recent payments";

  return (
    <Card data-testid="maintenance-recent-activity-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent activity</CardTitle>
        <p className="text-xs text-[#6F828A] mt-0.5">{rollup}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Featured row — single, calm "next action" surface */}
        {featured && needsAttention && (
          <div
            className="flex items-center justify-between gap-3 border border-[#EAE3D5] bg-[#FDFBF6] rounded-md px-3 py-2"
            data-testid="maintenance-next-action-row"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F4E1E]">Next action</div>
              <div className="text-sm text-[#2E3A3F] mt-0.5">
                <span className="font-medium">{fmtMoneyFromRow(featured)}</span>
                <span className="text-[#6F828A]"> · due {fmtDate(featured.due_date)}</span>
                {featuredArr?.child_name && (
                  <span className="text-[#6F828A]"> · {featuredArr.child_name}</span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => onRecord && onRecord(featured)}
              className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
              data-testid="maintenance-next-action-record"
            >
              Record payment
            </Button>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full text-left text-xs text-[#6F828A] hover:text-[#2E3A3F] inline-flex items-center gap-1 px-1 py-1"
          data-testid="maintenance-recent-toggle"
          aria-expanded={open}
        >
          {open ? (
            <>Hide full history <ChevronUp className="w-3.5 h-3.5" strokeWidth={1.8} /></>
          ) : (
            <>View full history ({recent.length}) <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.8} /></>
          )}
        </button>
        {open && (
          <PaymentList
            rows={recent}
            arrangements={arrangements}
            childName={childName}
            onRecord={onRecord}
            onShowHistory={onShowHistory}
          />
        )}
      </CardContent>
    </Card>
  );
}

// Helper — format an amount field from a recent-activity row using
// whichever currency is on the row or its arrangement. Keeps the
// "Next action" surface terse without re-implementing fmtMoney.
function fmtMoneyFromRow(row) {
  const cur = row.currency || "EUR";
  const amt = row.amount != null ? row.amount : row.amount_due;
  return fmtMoney(amt || 0, cur);
}



function SummaryCard({ label, value, tone, testid }) {
  const toneCls = tone === "paid"
    ? "bg-[#E0F2F2] border-[#A6D7D9]"
    : tone === "warn"
    ? "bg-[#FFE5D9] border-[#E6A481]"
    : "bg-white border-[#D9E1E3]";
  return (
    <div className={`p-3 rounded-md border ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
      <div className="font-heading text-xl font-bold text-[#0B2E32] mt-1">{value}</div>
    </div>
  );
}

function PaymentList({ rows, arrangements, childName, onRecord, onShowHistory, selected, onToggleSelected }) {
  return (
    <ul className="divide-y divide-[#EEF3F4]">
      {rows.map((p) => {
        const arr = arrangements.find((a) => a.id === p.arrangement_id);
        const cur = p.currency || "EUR";
        const isSelected = selected ? selected.has(p.id) : false;
        return (
          <li key={p.id} className={`py-3 flex flex-col sm:flex-row sm:items-center gap-2 ${isSelected ? "bg-[#F2FAFA] -mx-2 px-2 rounded-sm" : ""}`} data-testid={`maintenance-payment-row-${p.id}`}>
            {selected && (
              // v77.99.33 — Per-row checkbox for bulk selection. Plain
              // input rather than the shadcn Checkbox to keep tap
              // target large on mobile without extra wrapper styling.
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggleSelected(p.id)}
                className="w-4 h-4 accent-[#116B6E] flex-shrink-0 cursor-pointer"
                aria-label={`Select ${p.due_date}`}
                data-testid={`maintenance-payment-select-${p.id}`}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm whitespace-nowrap ${STATUS_TONE[p.status] || ""}`}>
                  {STATUS_LABEL[p.status] || p.status}
                </span>
                {/* v77.99.154 (iter 132.maintenance-dims) — Secondary
                    "Paid late" pill surfaces the timeliness axis when
                    the primary status doesn't. Prevents Part-paid or
                    Overpaid from silently hiding the fact a payment
                    landed after the due date. */}
                {p.timeliness === "paid_late" && p.status !== "paid_late" && (
                  <span
                    className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm whitespace-nowrap ${STATUS_TONE.paid_late}`}
                    data-testid={`maintenance-timeliness-paid-late-${p.id}`}
                  >
                    Paid late
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 whitespace-nowrap ${p.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
                  {p.is_shared ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {p.is_shared ? "Shared" : "Private"}
                </span>
              </div>
              <div className="mt-1 font-medium text-[#2E3A3F]">
                {fmtMoney(p.amount_due, cur)} · {fmtDate(p.due_date)}
              </div>
              <div className="text-xs text-[#6F828A]">
                {(arr?.child_ids || p.child_ids || []).map(childName).join(", ")}
                {p.amount_paid != null && ` · Paid ${fmtMoney(p.amount_paid, cur)} on ${fmtDate(p.paid_date)}`}
                {/* v77.99.154 — Show the outstanding balance next to
                    the paid amount when the record is part-paid. This
                    is what a solicitor / mediator reader needs at a
                    glance beside the two status chips. */}
                {p.outstanding > 0 && (
                  <span className="text-[#883417]" data-testid={`maintenance-outstanding-${p.id}`}>
                    {" · "}{fmtMoney(p.outstanding, cur)} outstanding
                  </span>
                )}
                {p.days_late > 0 && (
                  <span className="text-[#883417] font-medium" data-testid={`maintenance-days-late-${p.id}`}>
                    {" · "}{p.days_late} day{p.days_late === 1 ? "" : "s"} late
                  </span>
                )}
                {p.reference && ` · ${p.reference}`}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {/* v77.99.41 — Conditional label so the action is
                  obvious for both fresh occurrences and corrections.
                  A payment with a recorded status (paid/part_paid/
                  waived/disputed/paid_late) gets "Update status";
                  fresh / overdue / missed rows get "Record payment". */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRecord(p)}
                data-testid={`maintenance-record-button-${p.id}`}
              >
                {["paid", "paid_late", "part_paid", "overpaid", "waived", "disputed"].includes(p.status)
                  ? "Update status"
                  : "Record payment"}
              </Button>
              {/* History link — only shows when there's something to
                  show (i.e. once any record action has happened). */}
              {["paid", "paid_late", "part_paid", "overpaid", "waived", "disputed"].includes(p.status) && (
                <button
                  type="button"
                  className="text-[10px] text-[#116B6E] hover:underline"
                  onClick={() => onShowHistory?.(p)}
                  data-testid={`maintenance-history-button-${p.id}`}
                >
                  View history
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ArrangementCard({ arr, members, memberName, childName, onEdit }) {
  const cur = arr.currency || "EUR";
  return (
    <Card data-testid={`maintenance-arrangement-card-${arr.id}`}>
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base font-medium text-[#0B2E32] flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
            <ParentName
              id={arr.paying_parent_id}
              members={members}
              testid={`arrangement-${arr.id}-paying`}
            />
            <span aria-hidden="true">→</span>
            <ParentName
              id={arr.receiving_parent_id}
              members={members}
              testid={`arrangement-${arr.id}-receiving`}
            />
          </div>
          <div className="text-sm text-[#6F828A] mt-1 break-words">
            {arr.amount_mode === "per_child" ? (
              <>
                Per-child:{" "}
                {Object.entries(arr.per_child_amounts || {}).map(([cid, amt]) => `${childName(cid)} ${fmtMoney(amt, cur)}`).join(" · ")}
              </>
            ) : (
              <>{fmtMoney(arr.amount, cur)} (fixed total)</>
            )}
            {" · "}{FREQ_LABEL[arr.frequency]}
          </div>
          <div className="text-xs text-[#6F828A] mt-1">
            Children: {(arr.child_ids || []).map(childName).join(", ")} · Start {fmtDate(arr.start_date)}{arr.end_date ? ` · End ${fmtDate(arr.end_date)}` : ""}
          </div>
          {arr.notes && <div className="text-xs text-[#6F828A] mt-1 italic break-words">{arr.notes}</div>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 whitespace-nowrap ${arr.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
            {arr.is_shared ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {arr.is_shared ? "Shared" : "Private"}
          </span>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`maintenance-edit-arrangement-${arr.id}`}>Edit</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ArrangementSetupDialog({ open, onClose, onSaved, members, childList, editing, currentUserId }) {
  const blank = {
    paying_parent_id: "",
    receiving_parent_id: currentUserId || "",
    child_ids: [],
    amount_mode: "fixed_total",
    amount: "",
    per_child_amounts: {},
    currency: "EUR",
    frequency: "monthly",
    weekday: 0,
    day_of_month: 1,
    anchor_date: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    notes: "",
    grace_days: 3,
    is_shared: false,
    // v77.99.129 — Amendment cutoff. Populated only for existing
    // arrangements; the server uses it when money-affecting fields
    // change so historic occurrences keep their old amount.
    effective_date: "",
  };
  const [form, setForm] = useState(editing ? { ...blank, ...editing, amount: editing.amount ?? "", per_child_amounts: editing.per_child_amounts || {}, effective_date: new Date().toISOString().slice(0, 10) } : blank);
  const [saving, setSaving] = useState(false);

  // v77.99.129 — Detect money-affecting edits so we only surface the
  // "Effective date" callout (and only send the field) when it's
  // actually going to be used server-side. Any other kind of edit
  // (rename, notes, sharing toggle) leaves historic amounts intact.
  const moneyDirty = !!editing && (
    Number(form.amount || 0) !== Number(editing.amount || 0)
    || form.amount_mode !== (editing.amount_mode || "fixed_total")
    || form.currency !== (editing.currency || "EUR")
    || JSON.stringify(form.per_child_amounts || {}) !== JSON.stringify(editing.per_child_amounts || {})
  );

  const toggleChild = (cid) => {
    setForm((f) => ({
      ...f,
      child_ids: f.child_ids.includes(cid) ? f.child_ids.filter((x) => x !== cid) : [...f.child_ids, cid],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.amount = Number(payload.amount) || 0;
      payload.grace_days = Number(payload.grace_days) || 3;
      if (payload.amount_mode === "per_child") {
        payload.per_child_amounts = Object.fromEntries(
          Object.entries(payload.per_child_amounts || {}).map(([k, v]) => [k, Number(v) || 0]),
        );
      } else {
        payload.per_child_amounts = null;
      }
      if (!payload.end_date) delete payload.end_date;
      if (payload.frequency === "monthly") {
        payload.weekday = null;
        payload.anchor_date = null;
      } else if (payload.frequency === "weekly") {
        payload.day_of_month = null;
        payload.anchor_date = null;
      } else if (payload.frequency === "fortnightly") {
        payload.day_of_month = null;
      }
      if (editing) {
        // v77.99.129 — Only send effective_date on amendments where a
        // money-affecting field actually changed. Sending it on
        // cosmetic edits (rename, notes) would seed a no-op revision.
        if (!moneyDirty) {
          delete payload.effective_date;
        }
        await api.put(`/maintenance/arrangements/${editing.id}`, payload);
        toast.success(moneyDirty ? "Amendment saved — historic occurrences keep their original amount." : "Arrangement updated.");
      } else {
        // Never send effective_date on create — the initial state
        // simply lives on the top-level money fields.
        delete payload.effective_date;
        await api.post("/maintenance/arrangements", payload);
        toast.success("Arrangement created — private by default.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to save arrangement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit arrangement" : "New maintenance arrangement"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Paying parent</Label>
              <Select value={form.paying_parent_id} onValueChange={(v) => setForm({ ...form, paying_parent_id: v })}>
                <SelectTrigger data-testid="maintenance-paying-parent-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.is_placeholder ? " (placeholder)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Receiving parent</Label>
              <Select value={form.receiving_parent_id} onValueChange={(v) => setForm({ ...form, receiving_parent_id: v })}>
                <SelectTrigger data-testid="maintenance-receiving-parent-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.is_placeholder ? " (placeholder)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Linked children</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {childList.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => toggleChild(c.id)}
                  data-testid={`maintenance-child-chip-${c.id}`}
                  className={`text-xs px-2.5 py-1 border rounded-full whitespace-nowrap ${form.child_ids.includes(c.id) ? "bg-[#116B6E] text-white border-[#116B6E]" : "bg-white text-[#2E3A3F] border-[#D9E1E3]"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Amount type</Label>
            <Select value={form.amount_mode} onValueChange={(v) => setForm({ ...form, amount_mode: v })}>
              <SelectTrigger data-testid="maintenance-amount-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed_total">Fixed total amount</SelectItem>
                <SelectItem value="per_child">Amount per child</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.amount_mode === "fixed_total" ? (
            <div>
              <Label>Amount (EUR)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="maintenance-amount-input" />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Per-child amounts</Label>
              {form.child_ids.length === 0 ? (
                <div className="text-xs text-[#6F828A]">Link at least one child first.</div>
              ) : (
                form.child_ids.map((cid) => (
                  <div key={cid} className="flex items-center gap-2">
                    <span className="text-sm w-32 truncate">{childList.find((c) => c.id === cid)?.name || cid}</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.per_child_amounts[cid] ?? ""}
                      onChange={(e) => setForm({
                        ...form,
                        per_child_amounts: { ...form.per_child_amounts, [cid]: e.target.value },
                      })}
                      data-testid={`maintenance-per-child-amount-${cid}`}
                      className="max-w-[160px]"
                    />
                  </div>
                ))
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="maintenance-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="fortnightly">Fortnightly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.frequency === "monthly" && (
              <div>
                <Label>Day of month</Label>
                <Input type="number" min="1" max="31" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) || 1 })} data-testid="maintenance-day-of-month-input" />
              </div>
            )}
            {(form.frequency === "weekly" || form.frequency === "fortnightly") && (
              <div>
                <Label>Day of week</Label>
                <Select value={String(form.weekday ?? 0)} onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}>
                  <SelectTrigger data-testid="maintenance-weekday-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                      <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === "fortnightly" && (
              <div>
                <Label>Anchor date (first occurrence)</Label>
                <Input type="date" value={form.anchor_date || ""} onChange={(e) => setForm({ ...form, anchor_date: e.target.value })} data-testid="maintenance-anchor-date-input" />
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="maintenance-start-date-input" />
            </div>
            <div>
              <Label>End date (optional)</Label>
              <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="maintenance-end-date-input" />
            </div>
            <div>
              <Label>Grace days</Label>
              <Input type="number" min="0" max="30" value={form.grace_days} onChange={(e) => setForm({ ...form, grace_days: Number(e.target.value) || 0 })} data-testid="maintenance-grace-days-input" />
            </div>
          </div>

          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="maintenance-notes-input" />
          </div>

          {editing && moneyDirty ? (
            <div className="p-3 bg-[#FBF3E0] border border-[#E6CFA2] rounded-sm space-y-2" data-testid="maintenance-amendment-block">
              <div className="text-sm font-medium text-[#6F4E1E]">
                Amendment — effective from
              </div>
              <div className="text-[11px] text-[#6F4E1E]">
                Historic occurrences before this date keep the original amount so arrears stay accurate. Defaults to today.
              </div>
              <Input
                type="date"
                value={form.effective_date || ""}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                data-testid="maintenance-effective-date-input"
                className="max-w-[200px] bg-white"
              />
            </div>
          ) : null}

          <div className="flex items-center justify-between p-3 bg-[#F5F7F8] rounded-sm border border-[#D9E1E3]">
            <div>
              <div className="font-medium text-sm text-[#0B2E32]">Share this arrangement with co-parent</div>
              <div className="text-xs text-[#6F828A]">Private by default. Sharing is optional and reversible.</div>
            </div>
            <Switch checked={!!form.is_shared} onCheckedChange={(v) => setForm({ ...form, is_shared: v })} data-testid="maintenance-share-arrangement-switch" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="maintenance-save-arrangement-button">
              {saving ? "Saving…" : (editing ? "Save changes" : "Create arrangement")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentRecordDialog({ open, payment, arrangement, onClose, onSaved }) {
  // v77.99.41 — Pre-fill the form from any existing recorded status
  // so "Update status" never starts blank. Also remember the prior
  // status to power the Undo toast after a successful save.
  const isEditing = ["paid", "paid_late", "part_paid", "overpaid", "waived", "disputed"].includes(payment.status);
  const initialStatus = (() => {
    if (!isEditing) return "paid";
    // paid_late + overpaid still write as "paid" on the server side
    // — the late/overpaid label is computed from paid_date/amount.
    if (payment.status === "paid_late" || payment.status === "overpaid") return "paid";
    return payment.status;
  })();
  const [form, setForm] = useState({
    status: initialStatus,
    amount_paid: payment.amount_paid ?? payment.amount_due,
    paid_date: payment.paid_date || payment.due_date,
    method: payment.method || "",
    reference: payment.reference || "",
    note: payment.note || "",
    is_shared: !!payment.is_shared,
  });
  const [saving, setSaving] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const dialogTitle = isEditing
    ? `Update status · ${fmtDate(payment.due_date)}`
    : `Record payment · ${fmtDate(payment.due_date)}`;
  const saveLabel = isEditing ? "Save changes" : "Save";

  // v77.99.41 — Reset = soft-delete the recorded payment so the
  // schedule row falls back to its automatic status (due/late/missed).
  // Captures the prior snapshot so the Undo toast can restore it.
  const doReset = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/maintenance/payments/${encodeURIComponent(payment.id)}`);
      toast.success(
        `Status cleared. The payment is back to ${payment.status === "waived" || payment.status === "disputed" ? "awaiting" : "awaiting"}.`,
        {
          action: data?.previous
            ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    await api.post("/maintenance/payments/record", {
                      arrangement_id: payment.arrangement_id,
                      due_date: payment.due_date,
                      status: data.previous.status,
                      amount_paid: Number(data.previous.amount_paid) || 0,
                      paid_date: data.previous.paid_date || null,
                      method: data.previous.method || null,
                      reference: data.previous.reference || null,
                      note: data.previous.note || "",
                      is_shared: !!data.previous.is_shared,
                    });
                    toast.success("Status restored.");
                    onSaved();
                  } catch (err) {
                    toast.error("Couldn't restore the prior status — please re-enter it.");
                  }
                },
              }
            : undefined,
          duration: 6000,
        },
      );
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't clear the status.");
    } finally {
      setSaving(false);
      setConfirmingReset(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.status === "reset") {
      setConfirmingReset(true);
      return;
    }
    setSaving(true);
    try {
      const previousStatus = payment.status;
      await api.post("/maintenance/payments/record", {
        arrangement_id: payment.arrangement_id,
        due_date: payment.due_date,
        status: form.status,
        amount_paid: Number(form.amount_paid) || 0,
        paid_date: form.paid_date || null,
        method: form.method || null,
        reference: form.reference || null,
        note: form.note || "",
        is_shared: !!form.is_shared,
      });
      // v77.99.41 — Undo toast. When the user just marked the
      // occurrence as paid, the toast offers a single-click reset
      // back to the prior status (typically "awaiting"/due/overdue).
      // Six-second window. Audit history retains both actions.
      const verb = isEditing ? "Status updated" : "Payment recorded";
      const shareNote = form.is_shared ? " A neutral update was shared with your co-parent." : "";
      toast.success(`${verb}.${shareNote}`, {
        action: form.status === "paid"
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  await api.delete(`/maintenance/payments/${encodeURIComponent(payment.id)}`);
                  toast.success("Reverted to awaiting payment.");
                  onSaved();
                } catch (err) {
                  toast.error("Couldn't undo — please update the status manually.");
                }
              },
            }
          : undefined,
        duration: 6000,
      });
      void previousStatus;
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="maintenance-record-dialog-title">{dialogTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-sm text-[#6F828A]">
            Amount due: <span className="font-medium text-[#0B2E32]">{fmtMoney(payment.amount_due, arrangement?.currency || "EUR")}</span>
            {isEditing && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm bg-[#EEF3F4] border-[#D9E1E3] text-[#4A6170] align-middle" data-testid="maintenance-record-current-status">
                Currently: {payment.status_label || payment.status}
              </span>
            )}
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="maintenance-record-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Paid in full</SelectItem>
                <SelectItem value="part_paid">Part-paid</SelectItem>
                <SelectItem value="waived">Waived</SelectItem>
                <SelectItem value="disputed">Disputed</SelectItem>
                {isEditing && (
                  <SelectItem value="reset" data-testid="maintenance-record-reset-option">
                    Clear status (back to awaiting)
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {form.status === "reset" && (
              <p className="text-[11px] text-[#6F4E1E] mt-1" data-testid="maintenance-record-reset-hint">
                This will clear the recorded payment. The schedule row will revert to its automatic status (awaiting / overdue / missed). Audit history is preserved.
              </p>
            )}
          </div>
          {(form.status === "paid" || form.status === "part_paid") && (
            <>
              <div>
                <Label>Amount paid</Label>
                <Input type="number" step="0.01" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} data-testid="maintenance-record-amount-input" />
              </div>
              <div>
                <Label>Date paid</Label>
                <Input type="date" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} data-testid="maintenance-record-date-input" />
                {/* v77.99.37 — Inline preview when paid_date is after
                    due_date so the user knows the entry will land as
                    "Paid late" before they hit Save. Computed
                    locally; backend repeats the same logic on save. */}
                {form.paid_date && payment.due_date && form.paid_date > payment.due_date && (
                  <div className="mt-1 text-xs text-[#6F4E1E] inline-flex items-center gap-1" data-testid="maintenance-record-paid-late-preview">
                    <AlertTriangle className="w-3 h-3" strokeWidth={1.8} />
                    Will be recorded as <span className="font-medium">Paid late</span> · {Math.round((new Date(form.paid_date) - new Date(payment.due_date)) / 86400000)} day(s) after due date
                  </div>
                )}
              </div>
              <div>
                <Label>Method (optional)</Label>
                <Input value={form.method} placeholder="Bank transfer, cash…" onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="maintenance-record-method-input" />
              </div>
              <div>
                <Label>Reference (optional)</Label>
                <Input value={form.reference} placeholder="e.g. ref number" onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="maintenance-record-reference-input" />
              </div>
            </>
          )}
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="maintenance-record-note-input" />
          </div>

          <div className="flex items-center justify-between p-3 bg-[#F5F7F8] rounded-sm border border-[#D9E1E3]">
            <div>
              <div className="font-medium text-sm text-[#0B2E32]">Share this update with co-parent</div>
              <div className="text-xs text-[#6F828A]">A neutral notice will appear in their inbox. Private by default.</div>
            </div>
            <Switch checked={!!form.is_shared} onCheckedChange={(v) => setForm({ ...form, is_shared: v })} data-testid="maintenance-record-share-switch" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className={form.status === "reset" ? "bg-[#883417] hover:bg-[#6E2A12] text-white" : "bg-[#116B6E] hover:bg-[#0F5658] text-white"}
              data-testid="maintenance-record-save-button"
            >
              {saving
                ? "Saving…"
                : form.status === "reset"
                ? "Clear status"
                : saveLabel}
            </Button>
          </div>
        </form>
        {/* v77.99.41 — Reset confirmation. Two-step so an accidental
            "Clear status" + Save doesn't silently wipe a recorded
            payment. The Undo toast that fires after confirms still
            lets the user restore in one click within 6s. */}
        {confirmingReset && (
          <div className="mt-3 p-3 rounded-sm border border-[#E6A481] bg-[#FBEFED]" data-testid="maintenance-record-reset-confirm">
            <div className="text-sm text-[#883417] font-medium">Clear the recorded status?</div>
            <p className="text-xs text-[#7A2418] mt-1">
              This sets the payment back to <strong>awaiting</strong> and the schedule will reflect its automatic status. Audit history is preserved and you can Undo immediately after.
            </p>
            <div className="flex justify-end gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmingReset(false)} disabled={saving}>Keep current</Button>
              <Button size="sm" className="bg-[#883417] hover:bg-[#6E2A12] text-white" onClick={doReset} disabled={saving} data-testid="maintenance-record-reset-confirm-button">
                {saving ? "Clearing…" : "Yes, clear status"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// v77.99.33 — Bulk confirmation dialog. Shown after the user clicks
// "Mark as paid / waived / disputed" from the bulk action bar. Sums
// the amount across selected rows (best-effort frontend computation —
// the backend re-validates each row) and offers the standard
// privacy-first "Share this update with co-parent" toggle defaulting
// OFF.
function BulkConfirmDialog({ open, status, paymentIds, payments, onClose, onSaved }) {
  const targets = payments.filter((p) => paymentIds.includes(p.id));
  const totalDue = targets.reduce((acc, p) => acc + (Number(p.amount_due) || 0), 0);
  const cur = targets[0]?.currency || "EUR";
  const [isShared, setIsShared] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // v77.99.90 — Auto-batch progress when targets.length > 200. The
  // backend caps at 200 per request, so the UI quietly chops the
  // selection into 200-sized chunks and walks them sequentially with
  // a progress strip. The user only sees one atomic "Apply" action.
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const BATCH_SIZE = 200;
  const ACTION_LABELS = {
    paid: { verb: "Mark as paid", body: `Marking ${targets.length} payment${targets.length === 1 ? "" : "s"} as paid. The full amount due will be recorded as the amount paid for each.` },
    waived: { verb: "Mark as waived", body: `Marking ${targets.length} payment${targets.length === 1 ? "" : "s"} as waived.` },
    disputed: { verb: "Mark as disputed", body: `Marking ${targets.length} payment${targets.length === 1 ? "" : "s"} as disputed.` },
  };
  const cfg = ACTION_LABELS[status] || { verb: "Apply", body: "" };

  const submit = async () => {
    setSaving(true);
    // Slice the payload into BATCH_SIZE-sized chunks. For ≤200
    // targets this collapses to a single chunk, identical to the
    // pre-batched behaviour.
    const allPayments = targets.map((p) => ({ arrangement_id: p.arrangement_id, due_date: p.due_date }));
    const chunks = [];
    for (let i = 0; i < allPayments.length; i += BATCH_SIZE) {
      chunks.push(allPayments.slice(i, i + BATCH_SIZE));
    }
    setProgress({ done: 0, total: allPayments.length });
    let totalSucceeded = 0;
    let totalSkippedCount = 0;
    const allSkipped = [];
    let totalAmount = 0;
    let hardFailedAt = null; // index of the chunk that hit a hard error
    try {
      for (let idx = 0; idx < chunks.length; idx += 1) {
        const chunk = chunks[idx];
        try {
          const r = await api.post("/maintenance/payments/bulk-record", {
            payments: chunk, status, is_shared: isShared, note,
          });
          totalSucceeded += r.data?.succeeded || 0;
          totalSkippedCount += r.data?.skipped_count || 0;
          if (Array.isArray(r.data?.skipped)) allSkipped.push(...r.data.skipped);
          totalAmount += Number(r.data?.total_amount || 0);
          setProgress((p) => ({ ...p, done: p.done + chunk.length }));
        } catch (chunkErr) {
          hardFailedAt = idx;
          // Hard failure — stop, preserve what's done so the user can
          // re-select only the rest. Surface a clear partial-success.
          const detail = chunkErr?.response?.data?.detail;
          const sc = chunkErr?.response?.status;
          const msg = (sc === 500 || !chunkErr?.response)
            ? "We couldn't update all selected payments. Please try again or select fewer payments."
            : (typeof detail === "string" ? detail : "Bulk action failed.");
          toast.error(msg, totalSucceeded > 0 ? {
            description: `${totalSucceeded} payment${totalSucceeded === 1 ? "" : "s"} were updated before this stopped. The rest are still selected — try again.`,
          } : undefined);
          break;
        }
      }
      if (hardFailedAt === null) {
        if (totalSkippedCount > 0) {
          const firstReason = allSkipped[0]?.reason || "details unavailable";
          toast.info(`${totalSucceeded} updated, ${totalSkippedCount} skipped`, {
            description: `First skip reason: ${firstReason}. Open the activity log for full detail.`,
          });
        } else if (chunks.length > 1) {
          toast.success(`${totalSucceeded} updated across ${chunks.length} batches.`);
        } else {
          toast.success(`${totalSucceeded} updated.`);
        }
        onSaved();
      } else if (totalSucceeded > 0) {
        // Partial success — let the parent refresh so the UI reflects
        // the rows that DID get updated, even though the batch
        // stopped early.
        onSaved();
      }
    } finally {
      setSaving(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="maintenance-bulk-confirm-title">{cfg.verb}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2E3A3F]">{cfg.body}</p>
          <div className="p-3 bg-[#F5F7F8] border border-[#D9E1E3] rounded-sm">
            <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Total amount</div>
            <div className="font-heading text-xl font-bold text-[#0B2E32]" data-testid="maintenance-bulk-confirm-total">
              {fmtMoney(totalDue, cur)}
            </div>
            <div className="text-xs text-[#6F828A] mt-0.5">across {targets.length} occurrence{targets.length === 1 ? "" : "s"}</div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="maintenance-bulk-confirm-note" />
          </div>
          <div className="flex items-center justify-between p-3 bg-[#F5F7F8] rounded-sm border border-[#D9E1E3]">
            <div>
              <div className="font-medium text-sm text-[#0B2E32]">Share this update with co-parent</div>
              <div className="text-xs text-[#6F828A]">A neutral notice goes to their inbox per payment. Private by default.</div>
            </div>
            <Switch checked={isShared} onCheckedChange={setIsShared} data-testid="maintenance-bulk-confirm-share-switch" />
          </div>
          {saving && progress.total > BATCH_SIZE && (
            <div className="space-y-1.5" data-testid="maintenance-bulk-confirm-progress">
              <div className="flex items-center justify-between text-xs text-[#2E3A3F]">
                <span>Settling payments…</span>
                <span className="font-mono-data">{progress.done} of {progress.total}</span>
              </div>
              <div className="h-1.5 w-full bg-[#E4EAEB] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#116B6E] transition-all duration-200"
                  style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="button" onClick={submit} disabled={saving} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="maintenance-bulk-confirm-apply">
              {saving ? (progress.total > BATCH_SIZE ? `Settling ${progress.done}/${progress.total}…` : "Applying…") : cfg.verb}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// v77.99.33 — Settle-historic flow. Two-step: (1) user picks cut-off
// date and we fetch a preview from the server (count + total); (2)
// user reviews the impact and confirms. The backend computes the
// target list — the frontend just relays it.
function SettleHistoricDialog({ state, setState, onClose, onSaved }) {
  const [isShared, setIsShared] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const fetchPreview = async () => {
    setState({ ...state, loading: true });
    try {
      const r = await api.get(`/maintenance/payments/historic-preview?cutoff_date=${state.cutoff_date}`);
      setState({ ...state, preview: r.data, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load preview.");
      setState({ ...state, loading: false });
    }
  };
  const apply = async () => {
    setSaving(true);
    try {
      const r = await api.post("/maintenance/payments/bulk-settle-historic", {
        cutoff_date: state.cutoff_date,
        status: "paid",
        is_shared: isShared,
        note,
      });
      toast.success(`${r.data?.succeeded || 0} historic payment${(r.data?.succeeded || 0) === 1 ? "" : "s"} settled.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Settle failed.");
    } finally {
      setSaving(false);
    }
  };
  const preview = state.preview;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Settle historic payments</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#2E3A3F]">
            Mark every outstanding payment on or before a chosen cut-off date as paid. Future payments are untouched.
          </p>
          <div>
            <Label>Cut-off date</Label>
            <Input
              type="date"
              value={state.cutoff_date}
              onChange={(e) => setState({ ...state, cutoff_date: e.target.value, preview: null })}
              data-testid="maintenance-settle-historic-cutoff-input"
            />
          </div>
          {!preview && (
            <Button onClick={fetchPreview} disabled={state.loading} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="maintenance-settle-historic-preview-button">
              {state.loading ? "Loading…" : "Preview affected payments"}
            </Button>
          )}
          {preview && (
            <>
              <div className="p-3 bg-[#F5F7F8] border border-[#D9E1E3] rounded-sm">
                <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A]">Affected</div>
                <div className="font-heading text-xl font-bold text-[#0B2E32]" data-testid="maintenance-settle-historic-count">
                  {preview.count} payment{preview.count === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-[#6F828A] mt-0.5">
                  Total amount: <span className="font-medium text-[#0B2E32]" data-testid="maintenance-settle-historic-total">{fmtMoney(preview.total_amount, preview.currency)}</span>
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="maintenance-settle-historic-note" />
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F7F8] rounded-sm border border-[#D9E1E3]">
                <div>
                  <div className="font-medium text-sm text-[#0B2E32]">Share this update with co-parent</div>
                  <div className="text-xs text-[#6F828A]">A neutral notice goes to their inbox per payment.</div>
                </div>
                <Switch checked={isShared} onCheckedChange={setIsShared} data-testid="maintenance-settle-historic-share-switch" />
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            {preview && preview.count > 0 && (
              <Button type="button" onClick={apply} disabled={saving} className="bg-[#116B6E] hover:bg-[#0F5658] text-white" data-testid="maintenance-settle-historic-apply">
                {saving ? "Settling…" : `Settle ${preview.count} payment${preview.count === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
